import { useState } from "react";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { Avatar, Chip } from "@mui/material";
import BaseModal from "../../../../components/ui/BaseModal";
import BaseButton from "../../../../components/ui/BaseButton";
import BaseDataTableV2 from "../../../../components/ui/BaseDataTableV2";
import { showToast } from "../../../../store/toast/toastSlice";
import { fetchAvailableToLinkClients, linkClientToParent } from "../../../../services/api/client";

const columns = [
  {
    field: "id",
    headerName: "ID",
    width: 80,
    renderCell: (params) => <p className="table-font-size font-normal">{params.value}</p>,
  },
  {
    field: "clientName",
    headerName: "Name",
    width: 240,
    renderCell: (params) => (
      <div className="flex items-center">
        <Avatar sx={{ width: 28, height: 28, marginRight: 1 }} src={params?.row?.clientLogo} />
        <p className="table-font-size truncate max-w-44 overflow-hidden whitespace-nowrap">
          {params.value || "-"}
        </p>
      </div>
    ),
  },
  {
    field: "branchName",
    headerName: "Branch",
    width: 170,
    renderCell: (params) => <p className="table-font-size">{params?.row?.branch?.name || "-"}</p>,
  },
  {
    field: "teamName",
    headerName: "Service Delivery Team",
    width: 160,
    renderCell: (params) => <p className="table-font-size">{params?.row?.team?.name || "-"}</p>,
  },
  {
    field: "secondaryTeamName",
    headerName: "Operations Team",
    width: 160,
    renderCell: (params) => (
      <p className="table-font-size">{params?.row?.secondaryTeam?.name || "-"}</p>
    ),
  },
  {
    field: "country",
    headerName: "Country",
    width: 140,
    renderCell: (params) => (
      <p className="table-font-size">{params?.row?.address?.country || "-"}</p>
    ),
  },
  {
    field: "accountsPerson",
    headerName: "Accounts Person",
    width: 160,
    renderCell: (params) => (
      <p className="table-font-size">{params?.row?.account?.accountsPerson || "-"}</p>
    ),
  },
  {
    field: "paymentTerms",
    headerName: "Payment Terms",
    width: 140,
    renderCell: (params) => (
      <p className="table-font-size">{params?.row?.account?.paymentTerms || "-"}</p>
    ),
  },
  {
    field: "taxType",
    headerName: "Tax Type",
    width: 100,
    renderCell: (params) => (
      <p className="table-font-size">{params?.row?.account?.taxType || "-"}</p>
    ),
  },
  {
    field: "projectsCount",
    headerName: "Projects",
    width: 120,
    renderCell: (params) => (
      <p className="table-font-size font-normal">{params?.row?._count?.projects ?? 0}</p>
    ),
  },
  {
    field: "workOrdersCount",
    headerName: "Total Work Orders",
    width: 160,
    renderCell: (params) => (
      <p className="table-font-size font-normal">{params?.row?._count?.workOrders ?? 0}</p>
    ),
  },
  {
    field: "publishedWorkOrdersCount",
    headerName: "Published",
    width: 120,
    renderCell: (params) => (
      <p className="table-font-size font-normal">{params?.row?._count?.published ?? 0}</p>
    ),
  },
  {
    field: "assignedWorkOrdersCount",
    headerName: "Assigned",
    width: 120,
    renderCell: (params) => (
      <p className="table-font-size font-normal">{params?.row?._count?.assigned ?? 0}</p>
    ),
  },
  {
    field: "workDoneWorkOrdersCount",
    headerName: "Work Done",
    width: 120,
    renderCell: (params) => (
      <p className="table-font-size font-normal">{params?.row?._count?.workDone ?? 0}</p>
    ),
  },
  {
    field: "approvedWorkOrdersCount",
    headerName: "Approved",
    width: 120,
    renderCell: (params) => (
      <p className="table-font-size font-normal">{params?.row?._count?.approved ?? 0}</p>
    ),
  },
  {
    field: "paidWorkOrdersCount",
    headerName: "Paid",
    width: 120,
    renderCell: (params) => (
      <p className="table-font-size font-normal">{params?.row?._count?.paid ?? 0}</p>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    width: 130,
    renderCell: (params) =>
      params.row?.owner?.status === "APPROVED" ? (
        <Chip label="Active" color="primary" size="small" className="w-20 justify-center" />
      ) : params.row?.owner?.status === "DEACTIVATED" ? (
        <Chip label="In-Active" color="error" size="small" className="w-20 justify-center" />
      ) : params.row?.owner?.status === "PENDING" ? (
        <Chip
          label="Invited"
          color="primary"
          size="small"
          variant="outlined"
          className="w-20 justify-center"
        />
      ) : (
        "-"
      ),
  },
];

const LinkClientModal = ({ open, onClose, parentClientId, parentClientName, onLinked }) => {
  const dispatch = useDispatch();
  const [selectedIds, setSelectedIds] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  const { data, isLoading } = useQuery({
    queryKey: ["availableToLinkClients", paginationModel.page, paginationModel.pageSize],
    queryFn: () =>
      fetchAvailableToLinkClients({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      }),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const { mutateAsync: linkMutation, isPending: isLinking } = useMutation({
    mutationFn: ({ childIds, parentClientId }) => linkClientToParent(childIds, parentClientId),
  });

  const availableClients = data?.data?.data || [];
  const totalCount = data?.data?.totalCount || 0;

  const handleLink = async () => {
    if (selectedIds.length === 0) return;
    try {
      await linkMutation({ childIds: selectedIds.map(Number), parentClientId });
      dispatch(
        showToast({
          message: `${selectedIds.length} customer(s) linked successfully`,
          type: "success",
        }),
      );
      setSelectedIds([]);
      onLinked();
      onClose();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to link entities",
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseModal open={open} close={onClose} style={{ width: "min(95vw, 1300px)" }}>
      {/* Header */}
      <div className="px-8 py-5 border-b flex items-center justify-between">
        <div>
          <h6 className="text-dark font-medium">Link Entity</h6>
          <p className="text-sm text-gray-500 mt-0.5">
            Select one or more entities to link to{" "}
            <span className="font-semibold text-primary">
              "{parentClientName || "this parent"}"
            </span>
            .
          </p>
        </div>
        {selectedIds.length > 0 && (
          <span className="text-sm font-medium text-primary">{selectedIds.length} selected</span>
        )}
      </div>

      {/* Table */}
      <div className="px-6 py-4">
        <BaseDataTableV2
          className="max-h-[calc(100vh-380px)]"
          rowHeight={46}
          columns={columns}
          data={availableClients}
          isLoading={isLoading}
          disableColumnResize={false}
          checkboxSelection
          disableRowSelectionOnClick={false}
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={(newSelection) => setSelectedIds(newSelection)}
          hideFooter={false}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          rowCount={totalCount}
        />
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-center bg-white py-6 mt-3"
        style={{ boxShadow: "5px 0px 14px 5px rgba(0, 0, 0, 0.07)" }}
      >
        <BaseButton
          className="py-[5px] px-2 bg-[#E8E8E8] text-dark"
          fullWidth={false}
          onClick={onClose}
        >
          Cancel
        </BaseButton>
        <BaseButton
          loaderSize="22px"
          disabled={selectedIds.length === 0 || isLinking}
          isLoading={isLinking}
          className="py-[5px] px-4 ml-4 min-w-[100px]"
          color="primary"
          fullWidth={false}
          onClick={handleLink}
        >
          Link {selectedIds.length > 0 ? `(${selectedIds.length})` : ""} Customer
          {selectedIds.length !== 1 ? "s" : ""}
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default LinkClientModal;
